import React, { PureComponent } from "react";
import PropTypes from "prop-types";
import tw from "twin.macro";
import styled from "styled-components";
import connectComponent from "../../redux/connectComponent";
import { readablePhone } from "../../helpers/helper";
import { Link, Redirect } from "react-router-dom";

import { createToast, toastContainer } from "screens/shared/toast";
import Avatar from "screens/shared/avatar";

import PhoneField from "screens/shared/forms/phoneField";
import AddressAutocompleteField from "screens/shared/forms/addressAutocompleteField";

const PageContainer = styled.div`
  ${tw`mx-auto px-4 mt-6`}
  max-width: 1140px;

  @media (min-width: 576px) {
    max-width: 540px;
  }
  @media (min-width: 768px) {
    max-width: 720px;
  }
  @media (min-width: 992px) {
    max-width: 960px;
  }
  @media (min-width: 1200px) {
    max-width: 1140px;
  }
`;

const Card = styled(PageContainer)`
  ${tw`bg-white border border-solid rounded p-5`}
  border-color: rgba(0, 0, 0, 0.125);
`;

const FormGroup = styled.div`
  ${tw`mb-4`}
`;

const FormRow = styled.div`
  ${tw`flex flex-wrap -mx-1`}
`;

const FormCol = styled.div`
  ${tw`mb-4 px-1`}
  flex: 0 0 100%;
  max-width: 100%;

  ${({ md }) =>
    md &&
    `
    @media (min-width: 768px) {
      flex: 0 0 ${(md / 12) * 100}%;
      max-width: ${(md / 12) * 100}%;
    }
  `}
`;

const FormControl = styled.input`
  ${tw`block w-full`}
  padding: 0.375rem 0.75rem;
  font-size: 1rem;
  line-height: 1.5;
  color: #495057;
  background-color: #fff;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

  &:focus {
    color: #495057;
    background-color: #fff;
    border-color: #80bdff;
    outline: 0;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
  }
`;

const FormFileControl = styled.input`
  ${tw`block`}
`;

const BtnPrimary = styled.button`
  ${tw`inline-block text-center cursor-pointer text-white border border-solid`}
  font-weight: 400;
  padding: 0.375rem 0.75rem;
  font-size: 1rem;
  line-height: 1.5;
  border-radius: 0.25rem;
  background-color: #007bff;
  border-color: #007bff;
  transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out,
    border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

  &:hover {
    background-color: #0069d9;
    border-color: #0062cc;
  }

  &:focus {
    outline: 0;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
  }
`;

const BtnSuccess = styled(BtnPrimary)`
  background-color: #28a745;
  border-color: #28a745;

  &:hover {
    background-color: #218838;
    border-color: #1e7e34;
  }

  &:focus {
    box-shadow: 0 0 0 0.2rem rgba(40, 167, 69, 0.25);
  }
`;

// form helpers
const Label = (labelProps) => (
  <label htmlFor={labelProps.id}>{labelProps.children}</label>
);

const Input = (inputProps) => (
  <FormControl
    id={inputProps.id}
    ref={inputProps.inputRef}
    required={inputProps.required}
    type={inputProps.type || "text"}
    defaultValue={inputProps.value}
  />
);

const Field = (fieldProps) => (
  <FormCol md={fieldProps.md}>
    <Label id={fieldProps.id}>{fieldProps.label}</Label>
    <Input
      id={fieldProps.id}
      inputRef={fieldProps.inputRef}
      type={fieldProps.type || "text"}
      {...(fieldProps.inputProps || {})}
    />
  </FormCol>
);

class UserShow extends PureComponent {
  static propTypes = {
    actions: PropTypes.object
  };

  state = {
    contact: {},
    passwordType: "password",
    eyeClass: "fa-eye-slash",
    loggedOut: false,
    submitted: false
  };

  constructor(props) {
    super(props);
    this.emailRef = React.createRef();
    this.phoneRef = React.createRef();
    this.passwordRef = React.createRef();
    this.fileRef = React.createRef();
    this.formRef = React.createRef();

    // Home fields for update
    this.streetRef = React.createRef();
    this.unitNumberRef = React.createRef();
    this.cityRef = React.createRef();
    this.stateRef = React.createRef();
    this.postalCodeRef = React.createRef();
  }

  componentDidMount() {
    this.loadUser(this.userId(this.props));
  }

  loadUser = (id) => id && this.props.actions.loadUser(id);

  userId = (props) => props.database.authentication.userId();

  user = (props) => props.database.authentication.user();

  dataWithAvatar = (data) => {
    const avatar = this.fileRef.current.files[0];

    if (avatar) {
      return { ...data, avatar };
    } else {
      return data;
    }
  };

  handleForm = (event) => {
    event.preventDefault();

    const home = {
      ...(Object.keys(this.state.contact).length
        ? this.state.contact
        : {
            street: this.streetRef.current.value,
            city: this.cityRef.current.value,
            state: this.stateRef.current.value,
            postal_code: this.postalCodeRef.current.value
          })
    };

    this.props.actions
      .updateUser(
        this.dataWithAvatar({
          id: this.userId(this.props),
          email: this.emailRef.current.value,
          phone: this.phoneRef.current.value,
          password: this.passwordRef.current.value,
          ...home
        })
      )
      .then(() => createToast("Account settings updated!"))
      .then(() => this.props.actions.checkAuthentication())
      .then(() => this.setState({ submitted: true }));

    return false;
  };

  toggleVisibility = (event) => {
    event.preventDefault();
    this.setState(
      this.state.passwordType === "text"
        ? { passwordType: "password", eyeClass: "fa-eye-slash" }
        : { passwordType: "text", eyeClass: "fa-eye" }
    );
    return false;
  };

  logout = (event) => {
    event.preventDefault();
    this.setState({ loggedOut: true });
  };

  setAddress = (contact) => this.setState({ contact });

  // Autocomplete Address (for non-existing address)
  renderAddressField = (props) => {
    const user = this.user(props);

    return user.street?.length && user.postal_code?.length ? (
      <>
        <FormRow>
          <Field
            id="user-home-street"
            inputRef={this.streetRef}
            label="Street"
            inputProps={{
              size: 20,
              value: user.street,
              autoFill: "street-address",
              required: "required"
            }}
            md={8}
          />
        </FormRow>
        <FormRow>
          <Field
            id="user-home-city"
            inputRef={this.cityRef}
            label="City"
            inputProps={{ value: user.city, required: "required" }}
            md={6}
          />
          <Field
            id="user-home-state"
            inputRef={this.stateRef}
            label="State"
            inputProps={{ value: user.state, required: "required" }}
            md={3}
          />
          <Field
            id="user-home-postal_code"
            inputRef={this.postalCodeRef}
            label="Postal Code"
            inputProps={{
              value: user.postal_code,
              autoFill: "postal-code",
              required: "required"
            }}
            md={3}
          />
        </FormRow>
      </>
    ) : (
      <AddressAutocompleteField
        id="user_info_address"
        name="user_info[address]"
        placeholder="123 Main St, Northbrook, IL 90210"
        onChange={this.setAddress}
      />
    );
  };

  render() {
    const user = this.user(this.props);

    if (!user) return <div />;

    if (this.state.submitted) return <Redirect to="/account" />;
    if (this.state.loggedOut) return <Redirect to="/reset" />;

    return (
      <React.Fragment>
        {toastContainer()}
        <PageContainer>
          <Link to="/account">BACK</Link>
        </PageContainer>
        <Card>
          <h5>Update Account</h5>
          <form
            action="/"
            onSubmit={this.handleForm}
            ref={this.formRef}
            encType="multipart/form-data"
          >
            <Avatar {...this.props} type="users" {...user} size="medium" />
            <FormGroup style={{ marginTop: "1.5rem" }}>
              <FormFileControl type="file" ref={this.fileRef} />
            </FormGroup>
            <FormGroup>
              <Field
                id="user-email"
                inputRef={this.emailRef}
                label="Email"
                type="email"
                inputProps={{ required: true, value: user.email }}
              />
            </FormGroup>
            <FormGroup>
              <PhoneField
                id="user_info_phone"
                title="Mobile Phone"
                defaultValue={readablePhone(user.phone)}
                name="user_info[phone]"
                inputRef={this.phoneRef}
                placeholder=""
              />
            </FormGroup>
            <FormGroup>
              <label htmlFor="user_info_address">Address</label>
              {this.renderAddressField(this.props)}
            </FormGroup>
            <FormGroup>
              <label htmlFor="user_info_password">
                Password &nbsp;
                <a href="/" onClick={this.toggleVisibility}>
                  <i
                    className={"fa " + this.state.eyeClass}
                    aria-hidden={true}
                  ></i>
                </a>
              </label>
              <FormControl
                type={this.state.passwordType}
                id="user_info_password"
                name="user_info[password]"
                autoComplete="false"
                autofill="off"
                ref={this.passwordRef}
              />
            </FormGroup>

            <FormGroup>
              <BtnPrimary>Update</BtnPrimary>
            </FormGroup>

            <FormGroup>
              <BtnSuccess data-testid="signout" onClick={this.logout}>
                Logout
              </BtnSuccess>
            </FormGroup>
          </form>
        </Card>
      </React.Fragment>
    );
  }
}

export default connectComponent(UserShow, ["authentication", "users"]);
